import { Component, Input, Output, EventEmitter, inject, OnInit, HostListener, ElementRef, ViewChild } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { AvatarComponent } from "@shared/components/avatar/avatar.component";
import { SessionService } from "@shared/components/core/stores/session.service";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "@services/local-storage.service";
import { trigger, transition, style, animate } from "@angular/animations";

export type HeaderMode = "default" | "page";
type LangOpt = { code: 'pt'|'en'|'es'; label: string; flag: string };

const GLOBAL_LANG_KEY = 'appLang';
const userLangKey = (userId: string) => `appLang_${userId}`;
function normalizeLang(input?: string | null): LangOpt['code'] {
  const raw = (input ?? '').toLowerCase();
  if (raw.startsWith('pt')) return 'pt';
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'pt';
}

@Component({
  selector: "app-private-header",
  standalone: true,
  imports: [CommonModule, AvatarComponent, TranslateModule],
  templateUrl: "./private-header.component.html",
  styleUrl: "./private-header.component.scss",
  animations: [
    trigger('expandFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)', height: 0 }),
        animate('220ms cubic-bezier(.2,.8,.2,1)', style({ opacity: 1, transform: 'translateY(0)', height: '*' }))
      ]),
      transition(':leave', [
        animate('180ms cubic-bezier(.4,0,1,1)', style({ opacity: 0, transform: 'translateY(-4px)', height: 0 }))
      ]),
    ]),
  ],
})
export class PrivateHeaderComponent implements OnInit {
  @Input() mode: HeaderMode = "default";
  @Input() title = '';
  @Input() subtitle = '';
  @Input() titleKey?: string;
  @Input() subtitleKey?: string;
  @Input() titleParams?: Record<string, any>;
  @Input() subtitleParams?: Record<string, any>;
  @Input() icon: string | null = null;
  @Input() showBack = false;
  @Input() backTo?: string | (() => void);
  @Input() showMenuButton = false;
  @Output() menuClick = new EventEmitter<void>();

  private readonly loc = inject(Location);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly storage = inject(LocalStorageService);

  user = this.session.user;

  private readonly defaultFlag = '/assets/images/flags/br.svg';
  private readonly defaultLang: LangOpt = { code: 'pt', label: 'Português', flag: this.defaultFlag };

  langOpen = false;
  currentLang: LangOpt['code'] = this.defaultLang.code;
  selectedLang: LangOpt = this.defaultLang;

  languages: LangOpt[] = [
    { code: 'pt', label: 'Português',  flag: '/assets/images/flags/br.svg' },
    { code: 'en', label: 'English',    flag: '/assets/images/flags/us.svg' },
    { code: 'es', label: 'Español',    flag: '/assets/images/flags/es.svg' },
  ];

  @ViewChild('langRef', { static: false }) langRef?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    const userId = this.session.user()?.id ?? null;
    const savedUser = userId ? this.storage.get<string>(userLangKey(userId)) : null;
    const savedGlobal = this.storage.get<string>(GLOBAL_LANG_KEY);

    const candidate = normalizeLang(savedUser ?? savedGlobal ?? this.translate.currentLang ?? this.defaultLang.code);
    this.currentLang = this.languages.some(l => l.code === candidate) ? candidate : this.defaultLang.code;

    this.translate.use(this.currentLang);
    if (userId) this.storage.set(userLangKey(userId), this.currentLang);
    this.storage.set(GLOBAL_LANG_KEY, this.currentLang);

    this.updateSelectedLang();
  }

  toggleLang(): void { this.langOpen = !this.langOpen; }

  changeLanguage(lang: LangOpt['code']) {
    if (lang === this.currentLang) { this.langOpen = false; return; }

    const normalized = normalizeLang(lang);
    this.currentLang = normalized;
    this.translate.use(normalized);

    const userId = this.session.user()?.id ?? null;
    if (userId) this.storage.set(userLangKey(userId), normalized);
    this.storage.set(GLOBAL_LANG_KEY, normalized);

    this.updateSelectedLang();
    this.langOpen = false;
  }

  private updateSelectedLang() {
    this.selectedLang = this.languages.find(l => l.code === this.currentLang) || this.defaultLang;
  }

  onFlagError(ev: Event) { (ev.target as HTMLImageElement).src = this.defaultFlag; }


  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  onAnyPointerDown(ev: Event) {
    if (!this.langOpen) return;
    const target = ev.target as Node | null;
    const host = this.langRef?.nativeElement;
    if (host && target && !host.contains(target)) {
      this.langOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() { this.langOpen = false; }

  back(): void {
    if (typeof this.backTo === "string") { window.location.href = this.backTo; return; }
    if (typeof this.backTo === "function") { this.backTo(); return; }
    this.loc.back();
  }

  logout(): void { this.session.logout(); }

  avatarUrl(): string {
    const raw = this.user()?.photo?.trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    return raw.startsWith("/") ? raw : `/${raw}`;
  }
}
